import React, { useEffect, useMemo, useState } from "react";
import { Download, RefreshCcw } from "lucide-react";
import { supabase } from "../../../Services/supabaseClient";
import { useCompany } from "../../../context/CompanyContext";
import "./fixedAssets.css";
import { formatNaira, round2 } from "./faMoney";

function toISODate(d) {
  return d.toISOString().slice(0, 10);
}

function defaultPeriod() {
  // default to current year-to-date
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { start: toISODate(start), end: toISODate(end) };
}

function parseDate(iso) {
  // ISO YYYY-MM-DD
  return new Date(`${iso}T00:00:00Z`);
}

function isBefore(aISO, bISO) {
  return parseDate(aISO).getTime() < parseDate(bISO).getTime();
}
function isOnOrBefore(aISO, bISO) {
  return parseDate(aISO).getTime() <= parseDate(bISO).getTime();
}

function isBetweenInclusive(xISO, startISO, endISO) {
  const x = parseDate(xISO).getTime();
  const s = parseDate(startISO).getTime();
  const e = parseDate(endISO).getTime();
  return x >= s && x <= e;
}

function csvEscape(v) {
  const s = String(v ?? "");
  if (s.includes(",") || s.includes('"') || s.includes("\n"))
    return `"${s.replaceAll('"', '""')}"`;
  return s;
}

function downloadCSV(filename, rows) {
  const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function FixedAssetRollForward() {
  const { companyId } = useCompany();

  const { start: defaultStart, end: defaultEnd } = defaultPeriod();

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [category, setCategory] = useState("ALL");

  const [assets, setAssets] = useState([]);
  const [disposals, setDisposals] = useState([]);
  const [depLines, setDepLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  async function fetchData() {
    if (!companyId) return;
    setErr("");
    setLoading(true);

    // 1) Assets (include disposed assets too; we’ll filter in calculations)
    const aRes = await supabase
      .from("fixed_assets")
      .select("id, asset_tag, name, category, purchase_date, cost")
      .eq("company_id", companyId);

    if (aRes.error) {
      setErr(aRes.error.message);
      setLoading(false);
      return;
    }

    // 2) Disposals
    const dRes = await supabase
      .from("fixed_asset_disposals")
      .select("id, asset_id, disposal_date, proceeds, notes")
      .eq("company_id", companyId);

    if (dRes.error) {
      setErr(dRes.error.message);
      setLoading(false);
      return;
    }

    // 3) Depreciation lines
    const lRes = await supabase
      .from("fixed_asset_depreciation_lines")
      .select("id, asset_id, posting_date, depreciation_amount, run_id")
      .eq("company_id", companyId);

    if (lRes.error) {
      setErr(lRes.error.message);
      setLoading(false);
      return;
    }

    setAssets(aRes.data || []);
    setDisposals(dRes.data || []);
    setDepLines(lRes.data || []);
    setLoading(false);
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const categories = useMemo(() => {
    const set = new Set();
    for (const a of assets) if (a.category) set.add(a.category);
    return [
      "ALL",
      ...Array.from(set).sort((x, y) => String(x).localeCompare(String(y))),
    ];
  }, [assets]);

  const computed = useMemo(() => {
    if (!startDate || !endDate) return null;
    if (isBefore(endDate, startDate))
      return { error: "End date cannot be before start date." };

    // Build maps
    const assetById = new Map();
    for (const a of assets) {
      if (category !== "ALL" && (a.category || "") !== category) continue;
      assetById.set(a.id, a);
    }

    // disposal lookup: asset_id -> disposal record (if multiple, use earliest disposal date)
    const disposalByAsset = new Map();
    for (const d of disposals) {
      if (!assetById.has(d.asset_id)) continue;
      if (!d.disposal_date) continue;

      const prev = disposalByAsset.get(d.asset_id);
      if (!prev) disposalByAsset.set(d.asset_id, d);
      else {
        // choose earliest disposal date
        if (
          parseDate(d.disposal_date).getTime() <
          parseDate(prev.disposal_date).getTime()
        ) {
          disposalByAsset.set(d.asset_id, d);
        }
      }
    }

    // depreciation by asset:
    // - before start
    // - in period
    // - through disposal date (for disposed in period)
    const depBeforeStart = new Map();
    const depInPeriod = new Map();
    const depThroughDate = new Map(); // key `${assetId}|${dateISO}` -> sum

    for (const l of depLines) {
      if (!assetById.has(l.asset_id)) continue;
      if (!l.posting_date) continue;

      const amt = Number(l.depreciation_amount || 0);

      if (isBefore(l.posting_date, startDate)) {
        depBeforeStart.set(
          l.asset_id,
          (depBeforeStart.get(l.asset_id) || 0) + amt
        );
      }
      if (isBetweenInclusive(l.posting_date, startDate, endDate)) {
        depInPeriod.set(l.asset_id, (depInPeriod.get(l.asset_id) || 0) + amt);
      }

      // accumulate per asset for "through date" computation (we’ll compute on the fly)
      // We’ll store raw lines per asset:
    }

    // For disposal depreciation through disposal date, compute from raw lines per asset (more accurate)
    const depLinesByAsset = new Map();
    for (const l of depLines) {
      if (!assetById.has(l.asset_id)) continue;
      if (!l.posting_date) continue;
      const arr = depLinesByAsset.get(l.asset_id) || [];
      arr.push(l);
      depLinesByAsset.set(l.asset_id, arr);
    }
    for (const [, arr] of depLinesByAsset.entries()) {
      arr.sort(
        (x, y) =>
          parseDate(x.posting_date).getTime() -
          parseDate(y.posting_date).getTime()
      );
    }

    function sumDepThrough(assetId, throughISO) {
      const key = `${assetId}|${throughISO}`;
      if (depThroughDate.has(key)) return depThroughDate.get(key);

      const arr = depLinesByAsset.get(assetId) || [];
      let sum = 0;
      for (const l of arr) {
        if (isOnOrBefore(l.posting_date, throughISO))
          sum += Number(l.depreciation_amount || 0);
      }
      depThroughDate.set(key, sum);
      return sum;
    }

    // Determine opening/additions/disposals/closing sets
    const openingAssets = [];
    const additionsAssets = [];
    const disposalsInPeriod = [];
    const closingAssets = [];

    for (const a of assetById.values()) {
      if (!a.purchase_date) continue; // ignore incomplete

      const disp = disposalByAsset.get(a.id);
      const dispDate = disp?.disposal_date || null;

      const disposedBeforeStart = dispDate
        ? isBefore(dispDate, startDate)
        : false;
      const disposedInRange = dispDate
        ? isBetweenInclusive(dispDate, startDate, endDate)
        : false;
      const disposedByEnd = dispDate ? isOnOrBefore(dispDate, endDate) : false;

      // opening: purchased before start AND not disposed before start
      if (isBefore(a.purchase_date, startDate) && !disposedBeforeStart) {
        openingAssets.push(a);
      }

      // additions: purchased within period
      if (isBetweenInclusive(a.purchase_date, startDate, endDate)) {
        additionsAssets.push(a);
      }

      // disposals in period
      if (disposedInRange) {
        disposalsInPeriod.push({ asset: a, disposal: disp });
      }

      // closing: purchased on/before end AND not disposed by end
      if (isOnOrBefore(a.purchase_date, endDate) && !disposedByEnd) {
        closingAssets.push(a);
      }
    }

    const openingCost = round2(
      openingAssets.reduce((s, a) => s + Number(a.cost || 0), 0)
    );
    const additionsCost = round2(
      additionsAssets.reduce((s, a) => s + Number(a.cost || 0), 0)
    );
    const disposalsCost = round2(
      disposalsInPeriod.reduce((s, x) => s + Number(x.asset.cost || 0), 0)
    );
    const closingCost = round2(
      closingAssets.reduce((s, a) => s + Number(a.cost || 0), 0)
    );

    // Accum depreciation opening: sum dep before start for opening assets + also assets purchased before start even if disposed in period
    // (because they are part of opening position if not disposed before start)
    const openingAccumDep = round2(
      openingAssets.reduce(
        (s, a) => s + Number(depBeforeStart.get(a.id) || 0),
        0
      )
    );

    // Depreciation in period for assets that exist at any point in the period:
    // include closing assets + disposed-in-period assets + additions assets
    const assetIdsInPeriod = new Set([
      ...closingAssets.map((a) => a.id),
      ...additionsAssets.map((a) => a.id),
      ...disposalsInPeriod.map((x) => x.asset.id),
      ...openingAssets.map((a) => a.id),
    ]);

    let depThisPeriod = 0;
    for (const aid of assetIdsInPeriod)
      depThisPeriod += Number(depInPeriod.get(aid) || 0);
    depThisPeriod = round2(depThisPeriod);

    // Accum dep on disposals: depreciation through disposal date for each disposed-in-period asset
    const accumDepOnDisposals = round2(
      disposalsInPeriod.reduce((s, x) => {
        const dispDate = x.disposal?.disposal_date;
        if (!dispDate) return s;
        return s + sumDepThrough(x.asset.id, dispDate);
      }, 0)
    );

    const closingAccumDep = round2(
      openingAccumDep + depThisPeriod - accumDepOnDisposals
    );
    const closingNBV = round2(closingCost - closingAccumDep);

    // Breakdown by category (optional): summarize closing NBV per category
    const byCategory = new Map();
    for (const a of closingAssets) {
      const cat = a.category || "Uncategorized";
      const disp = disposalByAsset.get(a.id);
      const disposedByEnd = disp?.disposal_date
        ? isOnOrBefore(disp.disposal_date, endDate)
        : false;
      if (disposedByEnd) continue;

      const depToEnd = sumDepThrough(a.id, endDate);
      const nbv = round2(Number(a.cost || 0) - depToEnd);

      const prev = byCategory.get(cat) || {
        cost: 0,
        accum: 0,
        nbv: 0,
        count: 0,
      };
      prev.cost += Number(a.cost || 0);
      prev.accum += depToEnd;
      prev.nbv += nbv;
      prev.count += 1;
      byCategory.set(cat, prev);
    }

    const byCategoryRows = Array.from(byCategory.entries())
      .map(([cat, v]) => ({
        category: cat,
        count: v.count,
        closing_cost: round2(v.cost),
        closing_accum_dep: round2(v.accum),
        closing_nbv: round2(v.nbv),
      }))
      .sort(
        (a, b) =>
          b.closing_nbv - a.closing_nbv || a.category.localeCompare(b.category)
      );

    return {
      startDate,
      endDate,
      category,
      openingAssets,
      additionsAssets,
      disposalsInPeriod,
      closingAssets,
      openingCost,
      additionsCost,
      disposalsCost,
      closingCost,
      openingAccumDep,
      depThisPeriod,
      accumDepOnDisposals,
      closingAccumDep,
      closingNBV,
      byCategoryRows,
    };
  }, [assets, disposals, depLines, startDate, endDate, category]);

  function exportSummaryCSV() {
    if (!computed || computed.error) return;

    const rows = [
      ["KostPadi Fixed Asset Roll-Forward"],
      ["Period Start", computed.startDate],
      ["Period End", computed.endDate],
      ["Category", computed.category],
      [],
      ["Cost Roll-forward"],
      ["Opening Cost", computed.openingCost],
      ["Additions", computed.additionsCost],
      ["Disposals (Cost)", computed.disposalsCost],
      ["Closing Cost", computed.closingCost],
      [],
      ["Accumulated Depreciation Roll-forward"],
      ["Opening Accum Dep", computed.openingAccumDep],
      ["Depreciation (Period)", computed.depThisPeriod],
      ["Accum Dep on Disposals", computed.accumDepOnDisposals],
      ["Closing Accum Dep", computed.closingAccumDep],
      [],
      ["Closing NBV", computed.closingNBV],
    ];

    downloadCSV(
      `fixed-assets-rollforward-${computed.startDate}-to-${computed.endDate}.csv`,
      rows
    );
  }

  function exportDetailCSV() {
    if (!computed || computed.error) return;

    const rows = [
      ["Type", "Asset Tag", "Name", "Category", "Date", "Cost", "Proceeds"],
      ...computed.additionsAssets.map((a) => [
        "Addition",
        a.asset_tag,
        a.name,
        a.category || "",
        a.purchase_date,
        Number(a.cost || 0),
        "",
      ]),
      ...computed.disposalsInPeriod.map((x) => [
        "Disposal",
        x.asset.asset_tag,
        x.asset.name,
        x.asset.category || "",
        x.disposal?.disposal_date || "",
        Number(x.asset.cost || 0),
        Number(x.disposal?.proceeds || 0),
      ]),
    ];

    downloadCSV(
      `fixed-assets-rollforward-detail-${computed.startDate}-to-${computed.endDate}.csv`,
      rows
    );
  }

  return (
    <div className="fa-wrap">
      <div className="fa-header">
        <div className="fa-title">
          <h2>Fixed Assets Roll-Forward</h2>
          <p>
            PPE movement schedule for cost, accumulated depreciation, and NBV.
          </p>
        </div>

        <div className="fa-actions">
          <button className="fa-btn" onClick={fetchData} disabled={loading}>
            <RefreshCcw size={16} /> Refresh
          </button>
          <button
            className="fa-btn"
            onClick={exportSummaryCSV}
            disabled={!computed || computed?.error}
          >
            <Download size={16} /> Export Summary
          </button>
          <button
            className="fa-btn fa-btn-primary"
            onClick={exportDetailCSV}
            disabled={!computed || computed?.error}
          >
            <Download size={16} /> Export Detail
          </button>
        </div>
      </div>

      <div className="fa-panel">
        <div className="fa-toolbar">
          <div style={{ minWidth: 220 }}>
            <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
              Start Date
            </div>
            <input
              className="fa-input"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div style={{ minWidth: 220 }}>
            <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
              End Date
            </div>
            <input
              className="fa-input"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div style={{ minWidth: 260 }}>
            <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
              Category
            </div>
            <select
              className="fa-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c === "ALL" ? "All categories" : c}
                </option>
              ))}
            </select>
          </div>
        </div>

        {err && (
          <div style={{ marginTop: 10, color: "#ffb4b4", fontSize: 13 }}>
            {err}
          </div>
        )}
        {computed?.error && (
          <div style={{ marginTop: 10, color: "#ffb4b4", fontSize: 13 }}>
            {computed.error}
          </div>
        )}
        {loading && <div style={{ opacity: 0.8, padding: 10 }}>Loading…</div>}
      </div>

      {!loading && computed && !computed.error && (
        <>
          <div className="fa-grid">
            <div className="fa-card">
              <div className="label">Opening Cost</div>
              <div className="value">{formatNaira(computed.openingCost)}</div>
              <div className="hint">Assets at start</div>
            </div>
            <div className="fa-card">
              <div className="label">Additions</div>
              <div className="value">{formatNaira(computed.additionsCost)}</div>
              <div className="hint">Purchased in period</div>
            </div>
            <div className="fa-card">
              <div className="label">Disposals (Cost)</div>
              <div className="value">{formatNaira(computed.disposalsCost)}</div>
              <div className="hint">Cost removed</div>
            </div>
            <div className="fa-card">
              <div className="label">Closing Cost</div>
              <div className="value">{formatNaira(computed.closingCost)}</div>
              <div className="hint">Assets at end</div>
            </div>
          </div>

          <div className="fa-grid">
            <div className="fa-card">
              <div className="label">Opening Accum Dep</div>
              <div className="value">
                {formatNaira(computed.openingAccumDep)}
              </div>
              <div className="hint">Before start date</div>
            </div>
            <div className="fa-card">
              <div className="label">Depreciation (Period)</div>
              <div className="value">{formatNaira(computed.depThisPeriod)}</div>
              <div className="hint">From dep lines</div>
            </div>
            <div className="fa-card">
              <div className="label">Accum Dep on Disposals</div>
              <div className="value">
                {formatNaira(computed.accumDepOnDisposals)}
              </div>
              <div className="hint">Through disposal date</div>
            </div>
            <div className="fa-card">
              <div className="label">Closing NBV</div>
              <div className="value">{formatNaira(computed.closingNBV)}</div>
              <div className="hint">Closing cost - closing accum dep</div>
            </div>
          </div>

          <div className="fa-split">
            <div className="fa-panel">
              <div style={{ fontWeight: 900, marginBottom: 10 }}>
                Cost Roll-forward
              </div>
              <table className="fa-table">
                <thead>
                  <tr>
                    <th>Movement</th>
                    <th style={{ width: 220 }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Opening Cost</td>
                    <td>{formatNaira(computed.openingCost)}</td>
                  </tr>
                  <tr>
                    <td>+ Additions</td>
                    <td>{formatNaira(computed.additionsCost)}</td>
                  </tr>
                  <tr>
                    <td>- Disposals (Cost)</td>
                    <td>{formatNaira(computed.disposalsCost)}</td>
                  </tr>
                  <tr style={{ fontWeight: 900 }}>
                    <td>Closing Cost</td>
                    <td>{formatNaira(computed.closingCost)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="fa-panel">
              <div style={{ fontWeight: 900, marginBottom: 10 }}>
                Accumulated Depreciation Roll-forward
              </div>
              <table className="fa-table">
                <thead>
                  <tr>
                    <th>Movement</th>
                    <th style={{ width: 220 }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Opening Accum Dep</td>
                    <td>{formatNaira(computed.openingAccumDep)}</td>
                  </tr>
                  <tr>
                    <td>+ Depreciation (Period)</td>
                    <td>{formatNaira(computed.depThisPeriod)}</td>
                  </tr>
                  <tr>
                    <td>- Accum Dep on Disposals</td>
                    <td>{formatNaira(computed.accumDepOnDisposals)}</td>
                  </tr>
                  <tr style={{ fontWeight: 900 }}>
                    <td>Closing Accum Dep</td>
                    <td>{formatNaira(computed.closingAccumDep)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="fa-split">
            <div className="fa-panel">
              <div style={{ fontWeight: 900, marginBottom: 10 }}>
                Additions ({computed.additionsAssets.length})
              </div>
              <table className="fa-table">
                <thead>
                  <tr>
                    <th>Asset</th>
                    <th>Date</th>
                    <th style={{ width: 180 }}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {computed.additionsAssets.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <div style={{ fontWeight: 800 }}>{a.asset_tag}</div>
                        <div style={{ opacity: 0.8, fontSize: 12 }}>
                          {a.name}
                        </div>
                      </td>
                      <td>{a.purchase_date}</td>
                      <td>{formatNaira(a.cost)}</td>
                    </tr>
                  ))}
                  {computed.additionsAssets.length === 0 && (
                    <tr>
                      <td colSpan={3} style={{ opacity: 0.75 }}>
                        No additions in period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="fa-panel">
              <div style={{ fontWeight: 900, marginBottom: 10 }}>
                Disposals ({computed.disposalsInPeriod.length})
              </div>
              <table className="fa-table">
                <thead>
                  <tr>
                    <th>Asset</th>
                    <th>Disposal date</th>
                    <th style={{ width: 160 }}>Cost</th>
                    <th style={{ width: 160 }}>Proceeds</th>
                  </tr>
                </thead>
                <tbody>
                  {computed.disposalsInPeriod.map((x) => (
                    <tr key={x.disposal.id}>
                      <td>
                        <div style={{ fontWeight: 800 }}>
                          {x.asset.asset_tag}
                        </div>
                        <div style={{ opacity: 0.8, fontSize: 12 }}>
                          {x.asset.name}
                        </div>
                      </td>
                      <td>{x.disposal.disposal_date}</td>
                      <td>{formatNaira(x.asset.cost)}</td>
                      <td>{formatNaira(x.disposal.proceeds || 0)}</td>
                    </tr>
                  ))}
                  {computed.disposalsInPeriod.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ opacity: 0.75 }}>
                        No disposals in period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="fa-panel">
            <div style={{ fontWeight: 900, marginBottom: 10 }}>
              Closing Position by Category
            </div>
            <table className="fa-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th style={{ width: 120 }}>Count</th>
                  <th style={{ width: 180 }}>Closing Cost</th>
                  <th style={{ width: 200 }}>Closing Accum Dep</th>
                  <th style={{ width: 180 }}>Closing NBV</th>
                </tr>
              </thead>
              <tbody>
                {computed.byCategoryRows.map((r) => (
                  <tr key={r.category}>
                    <td>{r.category}</td>
                    <td>{r.count}</td>
                    <td>{formatNaira(r.closing_cost)}</td>
                    <td>{formatNaira(r.closing_accum_dep)}</td>
                    <td style={{ fontWeight: 900 }}>
                      {formatNaira(r.closing_nbv)}
                    </td>
                  </tr>
                ))}
                {computed.byCategoryRows.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ opacity: 0.75 }}>
                      No closing assets for selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
